import { useEffect, useState, useRef } from "react";
import api from "../../api/client";
import PortalLayout from "../layout/PortalLayout";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";

// ── Constants ─────────────────────────────────────────────────────────────────
const RELATIONSHIPS = [
  { value: "spouse",  label: "Spouse"  },
  { value: "parent",  label: "Parent"  },
  { value: "child",   label: "Child"   },
  { value: "sibling", label: "Sibling" },
  { value: "friend",  label: "Friend"  },
  { value: "other",   label: "Other"   },
];

const REL_COLORS = {
  spouse:  { bg: "#fdf2f8", color: "#9d174d" },
  parent:  { bg: "#eff6ff", color: "#1e40af" },
  child:   { bg: "#f0fdf4", color: "#166534" },
  sibling: { bg: "#fff7ed", color: "#9a3412" },
  friend:  { bg: "#f5f3ff", color: "#5b21b6" },
  other:   { bg: "var(--surface-bg)", color: "var(--text-secondary)" },
};

const ACCESS_OPTIONS = [
  {
    value: "view_only",
    label: "View only",
    desc: "Can view prescriptions only",
    icon: "pi-eye",
  },
  {
    value: "full_access",
    label: "Full access",
    desc: "View + request refills + upload",
    icon: "pi-unlock",
  },
];

const inputStyle = {
  width: "100%", fontSize: 13, padding: "9px 12px",
  border: "1px solid var(--border-color)", borderRadius: 10,
  fontFamily: "inherit", color: "var(--text-primary)",
  background: "var(--surface-card)", outline: "none",
  boxSizing: "border-box",
};

function getInitials(name = "") {
  return name.split(" ").map(w => w[0]).filter(Boolean).join("").slice(0, 2).toUpperCase() || "?";
}

function relLabel(value) {
  return RELATIONSHIPS.find(r => r.value === value)?.label ?? value;
}

// ── Incoming request banner ───────────────────────────────────────────────────
function IncomingRequestCard({ req, onRespond }) {
  const [responding, setResponding] = useState(false);

  const respond = async (action) => {
    setResponding(true);
    await onRespond(req.id, action);
    setResponding(false);
  };

  return (
    <div style={{
      background: "var(--surface-card)",
      border: "1.5px solid var(--brand-accent)",
      borderRadius: 12, padding: "14px 16px",
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: "var(--brand-light)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--brand-primary)", fontSize: 17, fontWeight: 600,
      }}>
        {getInitials(req.requester_name)}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text-primary)" }}>
          {req.requester_name}
          <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6, fontSize: 12 }}>
            {req.requester_phone}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
          Wants to add you as <b>{relLabel(req.relationship)}</b>
          {" · "}
          <span style={{
            fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 20,
            background: req.access_level === "full_access" ? "#fef3c7" : "#eff6ff",
            color: req.access_level === "full_access" ? "#92400e" : "#1e40af",
          }}>
            {req.access_level === "full_access" ? "Full access" : "View only"}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
        {/* Block — subtle, less prominent */}
        <button
          onClick={() => respond("block")}
          disabled={responding}
          title="Block this person from sending future requests"
          style={{
            padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
            border: "1px solid #fca5a5", background: "#fef2f2",
            color: "#b91c1c", cursor: "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", gap: 5,
          }}
        >
          <i className="pi pi-ban" style={{ fontSize: 11 }} />
          Block
        </button>
        <button
          onClick={() => respond("reject")}
          disabled={responding}
          style={{
            padding: "7px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 500,
            border: "1px solid var(--border-color)", background: "none",
            color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Decline
        </button>
        <button
          onClick={() => respond("accept")}
          disabled={responding}
          style={{
            padding: "7px 14px", borderRadius: 8, fontSize: 12.5, fontWeight: 500,
            border: "none", background: "var(--brand-primary)",
            color: "white", cursor: "pointer", fontFamily: "inherit",
            opacity: responding ? 0.7 : 1,
          }}
        >
          {responding ? <i className="pi pi-spin pi-spinner" /> : "Accept"}
        </button>
      </div>
    </div>
  );
}

// ── Member card ───────────────────────────────────────────────────────────────
function MemberCard({ member, onRemove, onAction }) {
  const rc = REL_COLORS[member.relationship] ?? REL_COLORS.other;
  const isPending  = member.status === "pending";
  const isRejected = member.status === "rejected";

  return (
    <div style={{
      background: "var(--surface-card)",
      border: `1px solid ${isPending ? "var(--border-color)" : "var(--border-color)"}`,
      borderRadius: 14, padding: "16px 18px",
      display: "flex", flexDirection: "column", gap: 12,
      opacity: isRejected ? 0.6 : 1,
      transition: "box-shadow 0.15s",
    }}
      onMouseEnter={e => !isRejected && (e.currentTarget.style.boxShadow = "0 4px 16px rgba(15,41,68,0.08)")}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      {/* Header */}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11, flexShrink: 0,
          background: "linear-gradient(135deg, var(--brand-primary), var(--brand-teal))",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "white", fontSize: 14, fontWeight: 600,
        }}>
          {getInitials(member.name)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text-primary)" }}>
            {member.name}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
              background: rc.bg, color: rc.color,
            }}>
              {relLabel(member.relationship)}
            </span>

            {/* Status badge */}
            {isPending && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#fef3c7", color: "#92400e" }}>
                <i className="pi pi-clock" style={{ fontSize: 9, marginRight: 3 }} />
                Awaiting consent
              </span>
            )}
            {isRejected && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#fef2f2", color: "#b91c1c" }}>
                Declined
              </span>
            )}
            {member.status === "accepted" && member.is_linked && (
              <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: "var(--brand-light)", color: "var(--brand-primary)" }}>
                <i className="pi pi-link" style={{ fontSize: 9, marginRight: 3 }} />
                {member.access_level === "full_access" ? "Full access" : "View only"}
              </span>
            )}
          </div>
        </div>

        {/* Remove/Cancel — only shown to the requester */}
        <button
          onClick={() => onRemove(member)}
          title={isPending ? "Cancel request" : "Remove"}
          style={{
            width: 28, height: 28, borderRadius: 7, flexShrink: 0,
            border: "1px solid var(--border-color)", background: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: 11,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.color = "#dc2626"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--text-muted)"; }}
        >
          <i className={isPending ? "pi pi-times" : "pi pi-trash"} />
        </button>
      </div>

      {member.phone && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 5 }}>
          <i className="pi pi-phone" style={{ fontSize: 11 }} />
          {member.phone}
        </div>
      )}

      {/* Action buttons — only when accepted */}
      {member.status === "accepted" && (
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          {member.is_linked && (
            <ActionBtn icon="pi-file-edit" label="Prescriptions" onClick={() => onAction("prescriptions", member)} />
          )}
          {member.is_linked && member.access_level === "full_access" && (
            <ActionBtn icon="pi-refresh" label="Refills" onClick={() => onAction("refill", member)} />
          )}
          {member.access_level === "full_access" && (
            <ActionBtn icon="pi-upload" label="Upload Rx" onClick={() => onAction("upload", member)} />
          )}
        </div>
      )}

      {/* Pending explainer */}
      {isPending && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Waiting for {member.name.split(" ")[0]} to accept your request.
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
      padding: "6px 10px", borderRadius: 8, fontSize: 12, fontWeight: 500,
      background: "var(--surface-bg)", color: "var(--text-secondary)",
      border: "1px solid var(--border-color)", cursor: "pointer", fontFamily: "inherit",
      transition: "all 0.15s", whiteSpace: "nowrap",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = "var(--brand-light)"; e.currentTarget.style.color = "var(--brand-primary)"; e.currentTarget.style.borderColor = "var(--brand-accent)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "var(--surface-bg)"; e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.borderColor = "var(--border-color)"; }}
    >
      <i className={`pi ${icon}`} style={{ fontSize: 11 }} />
      {label}
    </button>
  );
}

// ── Add Member Dialog ─────────────────────────────────────────────────────────
function AddMemberDialog({ visible, onHide, onAdded }) {
  const toast = useRef(null);
  const [mode, setMode]           = useState("search");
  const [phone, setPhone]         = useState("");
  const [searching, setSearching] = useState(false);
  const [found, setFound]         = useState(null);
  const [name, setName]           = useState("");
  const [relationship, setRelationship] = useState("other");
  const [accessLevel, setAccessLevel]   = useState("full_access");
  const [saving, setSaving]       = useState(false);

  const reset = () => {
    setMode("search"); setPhone(""); setFound(null);
    setName(""); setRelationship("other"); setAccessLevel("full_access");
  };
  const close = () => { reset(); onHide(); };

  const searchByPhone = async () => {
    if (!phone.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`relatives/find/?phone=${encodeURIComponent(phone.trim())}`);
      const d = res.data;
      if (d.found) setName(d.name);
      setFound(d);
    } catch { setFound({ found: false }); }
    finally { setSearching(false); }
  };

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), relationship,
        access_level: accessLevel,
        phone: phone.trim(),
        linked_account_id: (mode === "search" && found?.found && !found.existing_status)
          ? found.id : undefined,
      };
      const res = await api.post("relatives/", payload);
      onAdded(res.data);
      close();
    } catch (err) {
      toast.current?.show({
        severity: "error", summary: "Error",
        detail: err.response?.data?.error ?? "Could not add", life: 4000,
      });
    } finally { setSaving(false); }
  };

  const alreadyRequested = found?.existing_status;
  const showForm = mode === "manual" || (mode === "search" && found !== null && (!alreadyRequested || alreadyRequested === "rejected"));

  return (
    <Dialog
      header={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <i className="pi pi-user-plus" style={{ color: "var(--brand-primary)", fontSize: 16 }} />
          Add to Friends &amp; Family
        </div>
      }
      visible={visible} style={{ width: 540 }} onHide={close}
      breakpoints={{ "640px": "95vw" }}
    >
      <Toast ref={toast} />
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

        {/* Mode toggle */}
        <div style={{ display: "flex", background: "var(--surface-bg)", borderRadius: 10, padding: 4, gap: 4 }}>
          {[{ key: "search", icon: "pi-search", label: "Find by phone" }, { key: "manual", icon: "pi-user-edit", label: "Add manually" }].map(m => (
            <button key={m.key} onClick={() => { setMode(m.key); setFound(null); }}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                padding: "8px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 500,
                border: "none", cursor: "pointer", fontFamily: "inherit",
                background: mode === m.key ? "var(--surface-card)" : "transparent",
                color: mode === m.key ? "var(--brand-primary)" : "var(--text-muted)",
                boxShadow: mode === m.key ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              }}
            >
              <i className={`pi ${m.icon}`} style={{ fontSize: 12 }} /> {m.label}
            </button>
          ))}
        </div>

        {/* Phone search */}
        {mode === "search" && (
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Phone number
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} value={phone}
                onChange={e => { setPhone(e.target.value); setFound(null); }}
                placeholder="+94 71 234 5678"
                onKeyDown={e => e.key === "Enter" && searchByPhone()}
                onFocus={e => e.target.style.borderColor = "var(--brand-primary)"}
                onBlur={e => e.target.style.borderColor = "var(--border-color)"}
              />
              <button onClick={searchByPhone} disabled={searching || !phone.trim()}
                style={{
                  padding: "9px 16px", borderRadius: 10, fontSize: 13, fontWeight: 500,
                  background: "var(--brand-primary)", color: "white", border: "none",
                  cursor: "pointer", fontFamily: "inherit",
                  opacity: (!phone.trim() || searching) ? 0.6 : 1,
                }}
              >
                {searching ? <i className="pi pi-spin pi-spinner" /> : "Search"}
              </button>
            </div>

            {/* Not found */}
            {found && !found.found && (
              <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa", fontSize: 12.5, color: "#92400e" }}>
                <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                No portal account found.
                <button onClick={() => setMode("manual")} style={{ marginLeft: 6, background: "none", border: "none", color: "#92400e", cursor: "pointer", fontWeight: 600, fontSize: 12.5, textDecoration: "underline", fontFamily: "inherit" }}>
                  Add manually →
                </button>
              </div>
            )}

            {/* Already pending or accepted — block re-request */}
            {alreadyRequested && alreadyRequested !== "rejected" && (
              <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10, background: "#fef3c7", border: "1px solid #fde68a", fontSize: 12.5, color: "#92400e" }}>
                <i className="pi pi-clock" style={{ marginRight: 6 }} />
                You already sent a request to this person — it is currently <b>{alreadyRequested}</b>.
              </div>
            )}

            {/* Rejected — allow re-request */}
            {alreadyRequested === "rejected" && (
              <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa", fontSize: 12.5, color: "#92400e" }}>
                <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                Your previous request was declined. You can send a new request.
              </div>
            )}

            {/* Found */}
            {found?.found && !alreadyRequested && (
              <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 10, background: "var(--brand-light)", border: "1px solid var(--brand-accent)", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, var(--brand-primary), var(--brand-teal))", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 600 }}>
                  {getInitials(found.name)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{found.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{found.phone}</div>
                </div>
                <i className="pi pi-check-circle" style={{ marginLeft: "auto", color: "var(--status-success)", fontSize: 18 }} />
              </div>
            )}
          </div>
        )}

        {/* Manual phone */}
        {mode === "manual" && (
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Phone <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <input style={inputStyle} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+94 71 234 5678"
              onFocus={e => e.target.style.borderColor = "var(--brand-primary)"}
              onBlur={e => e.target.style.borderColor = "var(--border-color)"}
            />
          </div>
        )}

        {/* Name + relationship + access level */}
        {showForm && (
          <>
            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Full name <span style={{ color: "var(--status-danger)" }}>*</span>
              </label>
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Amara Silva"
                onFocus={e => e.target.style.borderColor = "var(--brand-primary)"}
                onBlur={e => e.target.style.borderColor = "var(--border-color)"}
              />
            </div>

            <div>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Relationship
              </label>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {RELATIONSHIPS.map(r => (
                  <button key={r.value} onClick={() => setRelationship(r.value)}
                    style={{
                      padding: "6px 13px", borderRadius: 20, fontSize: 12.5, fontWeight: 500,
                      border: `1.5px solid ${relationship === r.value ? "var(--brand-primary)" : "var(--border-color)"}`,
                      background: relationship === r.value ? "var(--brand-primary)" : "transparent",
                      color: relationship === r.value ? "white" : "var(--text-secondary)",
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >{r.label}</button>
                ))}
              </div>
            </div>

            {/* Access level — only for linked accounts */}
            {mode === "search" && found?.found && (
              <div>
                <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  Access level
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {ACCESS_OPTIONS.map(opt => (
                    <div key={opt.value} onClick={() => setAccessLevel(opt.value)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "11px 14px", borderRadius: 10, cursor: "pointer",
                        border: `1.5px solid ${accessLevel === opt.value ? "var(--brand-primary)" : "var(--border-color)"}`,
                        background: accessLevel === opt.value ? "var(--brand-light)" : "var(--surface-card)",
                        transition: "all 0.15s",
                      }}
                    >
                      <i className={`pi ${opt.icon}`} style={{ fontSize: 16, color: accessLevel === opt.value ? "var(--brand-primary)" : "var(--text-muted)", flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>{opt.label}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{opt.desc}</div>
                      </div>
                      {accessLevel === opt.value && (
                        <i className="pi pi-check-circle" style={{ marginLeft: "auto", color: "var(--brand-primary)", fontSize: 16 }} />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, padding: "9px 12px", borderRadius: 8, background: "var(--surface-bg)", fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 7 }}>
                  <i className="pi pi-shield" style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }} />
                  A connection request will be sent. They must accept before you can access their data.
                </div>
              </div>
            )}

            {/* Submit */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4, borderTop: "1px solid var(--border-light)" }}>
              <button onClick={close} style={{ padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: "none", border: "1px solid var(--border-color)", color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={submit} disabled={saving || !name.trim()}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500,
                  background: "var(--brand-primary)", color: "white", border: "none",
                  cursor: (saving || !name.trim()) ? "not-allowed" : "pointer", fontFamily: "inherit",
                  opacity: (saving || !name.trim()) ? 0.6 : 1,
                }}
              >
                <i className={`pi ${saving ? "pi-spin pi-spinner" : found?.found ? "pi-send" : "pi-user-plus"}`} style={{ fontSize: 12 }} />
                {saving ? "Saving…" : found?.found ? "Send request" : "Add to list"}
              </button>
            </div>
          </>
        )}

      </div>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FriendsFamily() {
  const navigate = useNavigate();
  const toast    = useRef(null);

  const [members, setMembers]         = useState([]);
  const [incoming, setIncoming]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [addVisible, setAddVisible]   = useState(false);
  const [removing, setRemoving]       = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [membersRes, incomingRes] = await Promise.all([
        api.get("relatives/"),
        api.get("relatives/incoming/"),
      ]);
      setMembers(membersRes.data);
      setIncoming(incomingRes.data);
    } catch {
      toast.current?.show({ severity: "error", summary: "Failed to load", life: 3000 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleRespond = async (relId, action) => {
    try {
      await api.post(`relatives/${relId}/respond/`, { action });
      const messages = {
        accept: { severity: "success", summary: "Connection accepted" },
        reject: { severity: "info",    summary: "Request declined" },
        block:  { severity: "warn",    summary: "Person blocked", detail: "They won't be able to send you future requests." },
      };
      const msg = messages[action];
      toast.current.show({ ...msg, life: 4000 });
      load();
    } catch {
      toast.current.show({ severity: "error", summary: "Could not respond", life: 3000 });
    }
  };

  const handleRemove = async (member) => {
    try {
      await api.delete(`relatives/${member.id}/`);
      setMembers(prev => prev.filter(m => m.id !== member.id));
      setRemoving(null);
      toast.current.show({ severity: "success", summary: "Removed", detail: `${member.name} removed.`, life: 3000 });
    } catch {
      toast.current.show({ severity: "error", summary: "Could not remove", life: 3000 });
    }
  };

  const handleAction = (action, member) => {
    const param = `?relative_id=${member.id}&name=${encodeURIComponent(member.name)}`;
    if (action === "prescriptions" || action === "refill") navigate(`/prescriptions${param}`);
    if (action === "upload") navigate(`/upload${param}`);
  };

  const accepted = members.filter(m => m.status === "accepted");
  const pending  = members.filter(m => m.status === "pending");
  const rejected = members.filter(m => m.status === "rejected");

  return (
    <PortalLayout>
      <Toast ref={toast} position="top-center" />

      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 className="page-title">Friends &amp; Family</h1>
          <p className="page-subtitle">Manage prescriptions and refills for your loved ones</p>
        </div>
        <button onClick={() => setAddVisible(true)}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500,
            background: "var(--brand-primary)", color: "white",
            border: "none", cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <i className="pi pi-user-plus" style={{ fontSize: 12 }} /> Add person
        </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, color: "var(--brand-primary)" }} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Incoming requests from others */}
          {incoming.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <i className="pi pi-bell" style={{ fontSize: 11 }} />
                Requests for you ({incoming.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {incoming.map(req => (
                  <IncomingRequestCard key={req.id} req={req} onRespond={handleRespond} />
                ))}
              </div>
            </div>
          )}

          {/* Active connections */}
          {accepted.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <i className="pi pi-users" style={{ fontSize: 11 }} />
                Connected ({accepted.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
                {accepted.map(m => <MemberCard key={m.id} member={m} onRemove={setRemoving} onAction={handleAction} />)}
              </div>
            </div>
          )}

          {/* Pending outgoing */}
          {pending.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <i className="pi pi-clock" style={{ fontSize: 11 }} />
                Awaiting response ({pending.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
                {pending.map(m => <MemberCard key={m.id} member={m} onRemove={setRemoving} onAction={handleAction} />)}
              </div>
            </div>
          )}

          {/* Empty state */}
          {accepted.length === 0 && pending.length === 0 && incoming.length === 0 && (
            <div className="ph-card" style={{ padding: "60px 20px", textAlign: "center" }}>
              <div style={{ width: 64, height: 64, borderRadius: 18, margin: "0 auto 16px", background: "var(--brand-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="pi pi-users" style={{ fontSize: 26, color: "var(--brand-primary)" }} />
              </div>
              <div style={{ fontWeight: 600, fontSize: 16, color: "var(--text-primary)", marginBottom: 8 }}>No one added yet</div>
              <div style={{ fontSize: 13.5, color: "var(--text-muted)", marginBottom: 22, maxWidth: 360, margin: "0 auto 22px" }}>
                Add family or friends to manage their prescriptions on their behalf.
              </div>
              <button onClick={() => setAddVisible(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 22px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: "var(--brand-primary)", color: "white", border: "none", cursor: "pointer", fontFamily: "inherit" }}
              >
                <i className="pi pi-user-plus" style={{ fontSize: 13 }} /> Add your first person
              </button>
            </div>
          )}

          {/* Rejected — shown dimly at bottom */}
          {rejected.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>
                Declined ({rejected.length})
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
                {rejected.map(m => <MemberCard key={m.id} member={m} onRemove={setRemoving} onAction={handleAction} />)}
              </div>
            </div>
          )}
        </div>
      )}

      <AddMemberDialog visible={addVisible} onHide={() => setAddVisible(false)}
        onAdded={(member) => {
          setMembers(prev => [...prev, member]);
          const msg = member.status === "pending"
            ? `Request sent to ${member.name}. Waiting for their consent.`
            : `${member.name} added to your list.`;
          toast.current.show({ severity: "success", summary: "Done", detail: msg, life: 5000 });
        }}
      />

      <Dialog header={removing?.status === "pending" ? "Cancel request" : "Remove from list"} visible={!!removing} style={{ width: 400 }} onHide={() => setRemoving(null)}>
        <div style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 20 }}>
          {removing?.status === "pending"
            ? <>Are you sure you want to cancel your connection request to <b>{removing?.name}</b>? They will not be notified.</>
            : <>Remove <b>{removing?.name}</b> from your list? This won't affect their portal account.</>
          }
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={() => setRemoving(null)} style={{ padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: "none", border: "1px solid var(--border-color)", color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit" }}>Keep</button>
          <button onClick={() => handleRemove(removing)} style={{ padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: "#dc2626", color: "white", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
            {removing?.status === "pending" ? "Cancel request" : "Remove"}
          </button>
        </div>
      </Dialog>
    </PortalLayout>
  );
}